import type { SynthesisStep, Chemical } from "@/types/chemistry";
import { getStepDisplayName } from "@/lib/stepUtils";

/** The following (downstream) step — i.e. the parent */
export function getFollowingStep(
  step: SynthesisStep,
  allSteps: SynthesisStep[]
): SynthesisStep | undefined {
  if (!step.parentId) return undefined;
  return allSteps.find((s) => s.id === step.parentId);
}

/** Preceding (upstream) steps — i.e. children whose parentId === step.id */
export function getPrecedingSteps(
  step: SynthesisStep,
  allSteps: SynthesisStep[]
): SynthesisStep[] {
  return allSteps.filter((s) => s.parentId === step.id);
}

export type ConnectedChemicalResult =
  | { type: "auto"; chemical: Chemical; fromStep: SynthesisStep }
  | { type: "choices"; chemicals: Chemical[]; fromStep: SynthesisStep }
  | null;

/**
 * Determine connected reactant from a preceding step's products.
 * If a preceding step has 1 product → auto. If >1 → choices.
 * Only considers the first preceding step that has products.
 */
export function getConnectedReactant(
  step: SynthesisStep,
  allSteps: SynthesisStep[]
): ConnectedChemicalResult {
  const preceding = getPrecedingSteps(step, allSteps);
  for (const prev of preceding) {
    if (prev.products.length === 1) {
      return { type: "auto", chemical: prev.products[0], fromStep: prev };
    }
    if (prev.products.length > 1) {
      return { type: "choices", chemicals: prev.products, fromStep: prev };
    }
  }
  return null;
}

/**
 * Determine connected product from the following step's reactants.
 * If the following step has 1 reactant → auto. If >1 → choices.
 */
export function getConnectedProduct(
  step: SynthesisStep,
  allSteps: SynthesisStep[]
): ConnectedChemicalResult {
  const following = getFollowingStep(step, allSteps);
  if (!following) return null;
  if (following.reactants.length === 1) {
    return { type: "auto", chemical: following.reactants[0], fromStep: following };
  }
  if (following.reactants.length > 1) {
    return { type: "choices", chemicals: following.reactants, fromStep: following };
  }
  return null;
}

/** Get a short label for a step */
export function getStepLabel(step: SynthesisStep): string {
  return getStepDisplayName(step);
}
